import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Button,
  Box,
  FormControl,
  TextField,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import DialogTransition from 'src/components/DialogTransition';
import DropdownMenu from 'src/components/DropdownMenu';
import { ContractPaymentForm } from 'src/utils/constants/contracts';
import {
  formatCreditCardDisplay,
} from 'src/utils/paymentMethodHelpers';
import { getPaymentFormOptions } from 'src/utils/contract';
import { translateBankAccountType, mapBankAccountsToOptions } from 'src/utils/bank-accounts';
import useStyles from '../../../styles';

const PaymentMethodEditDialog = ({
  open,
  onClose,
  onSave,
  isEditable,
  isSaving,
  canSave,
  currentPaymentForm,
  hasPendingAutomaticDebits,
  dialogPaymentForm,
  dialogBankAccountId,
  dialogSecondaryBankAccountId,
  dialogCardId,
  onPaymentFormChange,
  onBankAccountChange,
  onSecondaryBankAccountChange,
  onCardChange,
  validBankAccounts,
  creditCards,
  loadingCards,
  clabe
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const paymentFormOptions = useMemo(() => getPaymentFormOptions(), []);

  const translateBankAccountTypeFn = (type) => translateBankAccountType(type, t);

  const bankAccountOptions = useMemo(
    () => mapBankAccountsToOptions(validBankAccounts, translateBankAccountTypeFn),
    [validBankAccounts, t]
  );

  const secondaryBankAccountOptions = useMemo(
    () =>
      // exclude the bank account that is already selected as primary
      mapBankAccountsToOptions(
        validBankAccounts.filter((acc) => acc.id !== dialogBankAccountId),
        translateBankAccountTypeFn
      ),
    [validBankAccounts, dialogBankAccountId, t]
  );

    // Credit card options - using utility function for formatting
    const creditCardOptions = useMemo(
      () =>
        creditCards.map(card => ({
          key: card.id,
          name: formatCreditCardDisplay(card)
        })),
      [creditCards]
    );

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      onClose={onClose}
      TransitionComponent={DialogTransition}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.header}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {t('CONTRACT_VIEW.CONTRACT_DETAIL.EDIT_PAYMENT_METHOD')}
        </Typography>
      </DialogTitle>

      <DialogContent style={{ marginTop: 16 }}>
        <Grid container spacing={2}>
          {hasPendingAutomaticDebits &&
            currentPaymentForm === ContractPaymentForm.AUTOMATIC_DEBIT &&
            dialogPaymentForm !== ContractPaymentForm.AUTOMATIC_DEBIT && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {t('CONTRACT_VIEW.CONTRACT_DETAIL.PENDING_AUTOMATIC_DEBITS_WARNING')}
                </Alert>
              </Grid>
            )}

          <Grid item xs={12}>
            <FormControl fullWidth>
              <DropdownMenu
                list={paymentFormOptions}
                value={dialogPaymentForm}
                setValue={onPaymentFormChange}
                label={t('CONTRACT_VIEW.CONTRACT_DETAIL.PAYMENT_METHOD')}
                disabled={!isEditable}
              />
            </FormControl>
          </Grid>

          {dialogPaymentForm === ContractPaymentForm.AUTOMATIC_DEBIT && (
            <>
              {validBankAccounts.length > 0 ? (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <DropdownMenu
                        list={bankAccountOptions}
                        value={dialogBankAccountId}
                        setValue={onBankAccountChange}
                        label={t('CONTRACT_VIEW.CONTRACT_DETAIL.PRIMARY_BANK_ACCOUNT')}
                        disabled={!isEditable || validBankAccounts.length === 0}
                      />
                    </FormControl>
                  </Grid>
                  {validBankAccounts.length >= 2 && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <DropdownMenu
                          list={secondaryBankAccountOptions}
                          value={dialogSecondaryBankAccountId}
                          setValue={onSecondaryBankAccountChange}
                          label={t('CONTRACT_VIEW.CONTRACT_DETAIL.SECONDARY_BANK_ACCOUNT')}
                          disabled={
                            !isEditable ||
                            validBankAccounts.length < 2 ||
                            !dialogBankAccountId
                          }
                        />
                      </FormControl>
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error">
                    {t('CONTRACT_VIEW.CONTRACT_DETAIL.NO_BANK_ACCOUNTS_MESSAGE')}
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {dialogPaymentForm === ContractPaymentForm.CREDIT_CARD && (
            <>
              {creditCards.length > 0 ? (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <DropdownMenu
                      list={creditCardOptions}
                      value={dialogCardId}
                      setValue={onCardChange}
                      label={t('CONTRACT_VIEW.CONTRACT_DETAIL.CREDIT_CARD')}
                      isLoading={loadingCards}
                      disabled={!isEditable}
                    />
                  </FormControl>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error">
                    {t('CONTRACT_VIEW.CONTRACT_DETAIL.CREDIT_CARD_NO_CARDS_MESSAGE')}
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {dialogPaymentForm === ContractPaymentForm.BANK_TRANSFER && (
            <>
              {clabe ? (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('COMMON.CLABE')}
                    value={clabe}
                    disabled
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error">
                    {t('CONTRACT_VIEW.CONTRACT_DETAIL.TRANSFER_NO_CLABE_MESSAGE')}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            margin: '0.5rem auto'
          }}
        >
          <Button
            onClick={onClose}
            disabled={isSaving}
            color="primary"
            variant="outlined"
            sx={{ minWidth: '45%' }}
          >
            {t('COMMON.CANCEL')}
          </Button>
          <Button
            onClick={onSave}
            disabled={!canSave || isSaving}
            color="primary"
            variant="contained"
            sx={{ minWidth: '45%' }}
          >
            {t('COMMON.SAVE')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

PaymentMethodEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  canSave: PropTypes.bool.isRequired,
  currentPaymentForm: PropTypes.string,
  hasPendingAutomaticDebits: PropTypes.bool,
  dialogPaymentForm: PropTypes.string,
  dialogBankAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dialogSecondaryBankAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dialogCardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPaymentFormChange: PropTypes.func.isRequired,
  onBankAccountChange: PropTypes.func.isRequired,
  onSecondaryBankAccountChange: PropTypes.func.isRequired,
  onCardChange: PropTypes.func.isRequired,
  validBankAccounts: PropTypes.array.isRequired,
  creditCards: PropTypes.array.isRequired,
  loadingCards: PropTypes.bool,
  clabe: PropTypes.string
};

PaymentMethodEditDialog.defaultProps = {
  currentPaymentForm: null,
  hasPendingAutomaticDebits: false,
  dialogPaymentForm: null,
  dialogBankAccountId: null,
  dialogSecondaryBankAccountId: null,
  dialogCardId: null,
  loadingCards: false,
  clabe: null
};

export default PaymentMethodEditDialog;